import { useState } from 'react';
import styled from '@emotion/styled';
import { BUSINESS_INFO } from '@/common/businessInfo';
import { CONTENT_MAX_WIDTH, CONTENT_PADDING_X } from '@/styles/layout';

/**
 * 사이트 하단 — 로고·정책 링크·사업자 정보·저작권 (2026-09-29 시안 B안).
 * - 회사명·서비스명은 한 번씩만: 로고(명치사) · 사업자 정보의 상호 · "© 연도 명치사".
 * - 폰(앱 WebView 포함)은 사업자 정보를 [사업자 정보 ⌄]로 접어 두고, PC 는 두 줄로 늘 펼친다.
 *   PG 카드사 심사 항목(상호·대표자·사업자등록번호·주소·전화)은 펼치면 모두 보이고 PC 에서는 항상 보인다.
 * - 모바일은 하단 탭바(84px)가 고정되어 있으므로 그만큼 아래 여백을 둔다.
 */
const Wrap = styled.footer`
  width: 100%;
  background: #f0f3f2;
  border-top: 1px solid #e3e8e6;
  color: #66736F;
  font-size: 13px;
  line-height: 1.75;
  padding: 32px 0 36px;

  @media (max-width: 768px) {
    font-size: 12.5px;
    padding: 28px 0 calc(28px + 84px + env(safe-area-inset-bottom, 0px));

    /* 글 입력 중 키보드가 실제로 떠 있으면 하단 탭이 숨으므로 그 자리 여백도 걷는다 — 남겨 두면 맨 아래로 내렸을 때 키보드 위에
       회색 빈 영역(약 110pt)으로 보였다(2026-10-07 채용 등록, 새 앱). 포커스 직후가 아니라 키보드가 뜬 뒤에 걷어야 맨 아래 칸이
       튀지 않는다(common/inputKeyboard.ts 5, UI 가이드 7.1) */
    body[data-input-focused='true'][data-keyboard-up='true'] & {
      padding-bottom: 28px;
    }

    /* 글쓰기 화면(components/common/write)은 폰에서 화면을 꽉 채운다 — 도구 줄 아래에 푸터가 보이지 않게 */
    body[data-write-screen='true'] & {
      display: none;
    }

    /* 글 상세(폰)는 아래에 댓글 입력이 붙어 있어 푸터를 보이지 않는다(사용자 지시 "하단 푸터 정보는 미노출") */
    body[data-comment-bar='true'] & {
      display: none;
    }
  }
`;

/** PC: 왼쪽 로고·사업자 정보 / 오른쪽 정책 링크. 폰: 로고 → 정책 링크 → [사업자 정보] */
const Inner = styled.div`
  max-width: ${CONTENT_MAX_WIDTH};
  margin: 0 auto;
  padding: 0 ${CONTENT_PADDING_X};
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-areas: 'brand links' 'body links';
  column-gap: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    grid-template-areas: 'brand' 'links' 'body';
    padding: 0 20px;
  }
`;

const Brand = styled.div`
  font-size: 17px;
  font-weight: 800;
  color: #4f5b58;
  letter-spacing: -0.02em;
  margin-bottom: 12px;
  grid-area: brand;

  i {
    display: inline-block;
    width: 6px;
    height: 6px;
    margin-left: 2px;
    border-radius: 50%;
    background: #5a9b96;
  }
`;

const Links = styled.nav`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  font-size: 13px;
  align-self: start;
  grid-area: links;

  a {
    color: #4f5b58;
    text-decoration: none;
  }

  a:first-of-type {
    font-weight: 700;
    color: #1f2a28;
  }

  @media (max-width: 768px) {
    margin-bottom: 14px;
  }
`;

const Toggle = styled.button`
  display: none;
  align-items: center;
  gap: 4px;
  padding: 0;
  margin-bottom: 6px;
  border: 0;
  background: none;
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  color: #4f5b58;
  cursor: pointer;

  svg {
    transition: transform 0.2s ease;
  }

  &[aria-expanded='true'] svg {
    transform: rotate(180deg);
  }

  @media (max-width: 768px) {
    display: inline-flex;
  }
`;

const Info = styled('div', { shouldForwardProp: (prop: string) => prop !== 'open' })<{ open: boolean }>`
  span {
    white-space: nowrap;
  }

  .sep {
    color: #c7cfcc;
    margin: 0 6px;
  }

  a {
    color: inherit;
  }

  /* PC 는 늘 펼침, 폰은 [사업자 정보] 를 눌렀을 때만 */
  @media (max-width: 768px) {
    display: ${({ open }) => (open ? 'block' : 'none')};
    margin-bottom: 4px;

    /* 폰은 항목 묶음마다 줄을 바꾼다(PC 의 두 줄 안 구분선 대신) */
    .pc-sep {
      display: none;
    }

    .m-break {
      display: block;
    }

    br {
      display: none;
    }
  }
`;

const Copy = styled.div`
  margin-top: 12px;
  font-size: 12px;
  color: #9aa5a2;

  @media (max-width: 768px) {
    margin-top: 6px;
  }
`;

const Chevron = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const Footer = () => {
  const [open, setOpen] = useState<boolean>(false);
  const { companyName, serviceName, representative, registrationNumber, mailOrderSalesNumber, address, phone, email } = BUSINESS_INFO;

  return (
    <Wrap>
      <Inner>
        <Brand>
          {serviceName}
          <i aria-hidden />
        </Brand>
        <div style={{ gridArea: 'body' }}>
          <Toggle type="button" aria-expanded={open} aria-controls="footer-business-info" onClick={() => setOpen((v) => !v)}>
            사업자 정보
            <Chevron />
          </Toggle>
          <Info id="footer-business-info" open={open}>
            {/* 1줄: 상호 · 대표 · 사업자등록번호(· 통신판매업) */}
            <span className="m-break">
              <span>{companyName}</span>
              <span className="sep">|</span>
              <span>대표 {representative}</span>
              <span className="sep pc-sep">|</span>
            </span>
            <span className="m-break">
              <span>사업자등록번호 {registrationNumber}</span>
              {mailOrderSalesNumber && (
                <>
                  <span className="sep">|</span>
                  <span>통신판매업신고번호 {mailOrderSalesNumber}</span>
                </>
              )}
            </span>
            <br />
            {/* 2줄: 주소 · 전화 · 이메일 */}
            {address && (
              <span className="m-break">
                <span>{address}</span>
                <span className="sep pc-sep">|</span>
              </span>
            )}
            <span className="m-break">
              {phone && (
                <>
                  <span>전화 {phone}</span>
                  <span className="sep">|</span>
                </>
              )}
              <a href={`mailto:${email}`}>{email}</a>
            </span>
          </Info>
          <Copy>© {new Date().getFullYear()} {serviceName}</Copy>
        </div>
        <Links aria-label="정책 링크">
          <a href="/privacy-policy">개인정보처리방침</a>
          <a href="/terms-of-service">이용약관</a>
          <a href="/refund-policy">결제·환불정책</a>
          <a href="/inquiry">문의하기</a>
        </Links>
      </Inner>
    </Wrap>
  );
};

export default Footer;
